import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CheckSquare,
  UserCheck,
  User,
  Sun,
  Clock,
  AlertTriangle,
  Calendar,
  BarChart2,
  ShieldCheck,
  Star,
  Users,
  Network,
  Folder,
  Tag,
  Trash2,
  Plus,
  Search,
  LayoutGrid,
  List as ListIcon,
  Play,
  Square,
  MoreVertical,
  CheckCircle2,
  Circle,
  FolderKanban,
  ArrowUpDown,
  Filter,
  RefreshCw,
  Share2,
  CircleDot,
  Flag,
  X,
} from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import api from '../../api/client';
import TaskCreateModal from '../../components/tasks/TaskCreateModal';
import TaskDetailDrawer from '../../components/tasks/TaskDetailDrawer';
import TaskKanbanBoard from '../../components/tasks/TaskKanbanBoard';
import TaskCalendarView from '../../components/tasks/TaskCalendarView';
import TaskTimelineView from '../../components/tasks/TaskTimelineView';
import TaskShareModal from '../../components/tasks/TaskShareModal';

export default function TasksApp({ defaultView = null }) {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const currentView = defaultView || searchParams.get('view') || (location.pathname.endsWith('/calendar') ? 'calendar' : location.pathname.endsWith('/timeline') ? 'timeline' : 'dashboard');

  const [viewMode, setViewMode] = useState('list'); // 'list' or 'board'
  const [tasks, setTasks] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Modals & Drawers
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalStatus, setCreateModalStatus] = useState('TODO');
  const [detailDrawerTaskId, setDetailDrawerTaskId] = useState(null);
  const [shareModalTask, setShareModalTask] = useState(null);

  // Load Dashboard or Tasks based on view
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, catRes, timerRes] = await Promise.all([
        api.get('/projects?filter=all').catch(() => ({ data: [] })),
        api.get('/tasks/categories').catch(() => ({ data: [] })),
        api.get('/tasks/timer/active').catch(() => ({ data: null })),
      ]);
      setProjects(projRes.data || []);
      setCategories(catRes.data || []);

      if (timerRes.data) {
        setActiveTimer(timerRes.data);
        setTimerSeconds(timerRes.data.elapsedSeconds || 0);
      } else {
        setActiveTimer(null);
      }

      if (currentView === 'dashboard') {
        const dashRes = await api.get('/tasks/dashboard');
        setDashboardData(dashRes.data);
      } else {
        const tasksRes = await api.get('/tasks', {
          params: {
            view: currentView,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            priority: priorityFilter !== 'all' ? priorityFilter : undefined,
            categoryId: categoryFilter !== 'all' ? categoryFilter : undefined,
            search: searchQuery.trim() || undefined,
          },
        });
        setTasks(tasksRes.data || []);
      }
    } catch (err) {
      console.error('Failed to load tasks data:', err);
    } finally {
      setLoading(false);
    }
  }, [currentView, statusFilter, priorityFilter, categoryFilter, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Timer ticker
  useEffect(() => {
    let interval = null;
    if (activeTimer) {
      interval = setInterval(() => {
        setTimerSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTimer]);

  const handleStopActiveTimer = async () => {
    if (!activeTimer) return;
    try {
      await api.post(`/tasks/${activeTimer.task_id}/timer/stop`);
      setActiveTimer(null);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to stop timer');
    }
  };

  const handleTaskStatusChange = async (taskId, nextStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: nextStatus });
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update task status');
    }
  };

  const handleToggleFavorite = async (e, task) => {
    e.stopPropagation();
    try {
      await api.post(`/tasks/${task.id}/favorite`);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRestoreTask = async (e, taskId) => {
    e.stopPropagation();
    try {
      await api.post(`/tasks/${taskId}/restore`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to restore task');
    }
  };

  const formatTimerClock = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getViewTitle = () => {
    switch (currentView) {
      case 'dashboard': return 'Tasks Dashboard';
      case 'my-tasks': return 'My Tasks';
      case 'assigned-to-me': return 'Assigned To Me';
      case 'created-by-me': return 'Created By Me';
      case 'today': return "Today's Schedule";
      case 'upcoming': return 'Upcoming Tasks';
      case 'overdue': return 'Overdue Tasks';
      case 'calendar': return 'Calendar View';
      case 'timeline': return 'Gantt Timeline';
      case 'completed': return 'Completed Tasks';
      case 'favorites': return 'Favorite Tasks';
      case 'shared-with-me': return 'Shared With Me';
      case 'shared-by-me': return 'Shared By Me';
      case 'categories': return 'Task Categories';
      case 'tags': return 'Tags & Labels';
      case 'trash': return 'Trash (Deleted Tasks)';
      default: return 'Tasks';
    }
  };

  return (
    <MainLayout>
      {/* Top Header & Actions - Full Bleed Edge-to-Edge */}
      <div className="tasks-hero-header notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <h1 className="notes-hero-title">
              {getViewTitle()}
            </h1>
            <p className="notes-hero-subtitle">
              Manage projects, collaborate with teammates, and track schedules effortlessly.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {currentView !== 'dashboard' && currentView !== 'calendar' && currentView !== 'timeline' && (
              <div style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.15)', padding: '3px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
                <button
                  onClick={() => setViewMode('list')}
                  style={{
                    background: viewMode === 'list' ? '#ffffff' : 'transparent',
                    color: viewMode === 'list' ? '#0f766e' : '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  <ListIcon size={15} />
                  <span>List</span>
                </button>
                <button
                  onClick={() => setViewMode('board')}
                  style={{
                    background: viewMode === 'board' ? '#ffffff' : 'transparent',
                    color: viewMode === 'board' ? '#0f766e' : '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  <LayoutGrid size={15} />
                  <span>Board</span>
                </button>
              </div>
            )}

            <button
              onClick={() => {
                setCreateModalStatus('TODO');
                setCreateModalOpen(true);
              }}
              className="btn-primary notes-hero-btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <Plus size={16} />
              <span>New Task</span>
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Active Timer Banner */}
        {activeTimer && (
          <div
            style={{
              padding: '12px 20px',
              borderRadius: '12px',
              background: 'linear-gradient(90deg, rgba(6, 182, 212, 0.15), rgba(59, 130, 246, 0.15))',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--accent-primary)', animation: 'pulse 1.5s infinite' }} />
              <Clock size={18} color="var(--accent-primary)" />
              <div>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Tracking Time on: </span>
                <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{activeTimer.task_title}</strong>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, fontFamily: 'monospace', color: 'var(--accent-primary)' }}>
                {formatTimerClock(timerSeconds)}
              </div>
              <button
                onClick={handleStopActiveTimer}
                className="btn-primary"
                style={{ padding: '6px 14px', fontSize: '12px', background: 'var(--danger)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <Square size={14} fill="currentColor" />
                <span>Stop Timer</span>
              </button>
            </div>
          </div>
        )}

        {/* ──────────────── DASHBOARD VIEW ──────────────── */}
        {currentView === 'dashboard' && dashboardData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* 8 Metric Cards Grid - 2 per row horizontal scrolling on mobile */}
            <div className="tasks-metrics-scroll-grid">
              {[
                { label: 'Total Tasks', value: dashboardData.metrics.totalTasks, icon: CheckSquare, color: '#3b82f6' },
                { label: 'My Tasks', value: dashboardData.metrics.myTasks, icon: User, color: '#06b6d4' },
                { label: 'Due Today', value: dashboardData.metrics.todayTasks, icon: Sun, color: '#f59e0b' },
                { label: 'Upcoming', value: dashboardData.metrics.upcomingTasks, icon: Clock, color: '#8b5cf6' },
                { label: 'Overdue', value: dashboardData.metrics.overdueTasks, icon: AlertTriangle, color: '#ef4444' },
                { label: 'Completed', value: dashboardData.metrics.completedTasks, icon: ShieldCheck, color: '#10b981' },
                { label: 'Projects', value: dashboardData.metrics.projects, icon: FolderKanban, color: '#ec4899' },
                { label: 'Team Tasks', value: dashboardData.metrics.teamTasks, icon: Users, color: '#6366f1' },
              ].map((card, i) => {
                const CardIcon = card.icon;
                return (
                  <div
                    key={i}
                    className="tasks-metric-card"
                  >
                    <div className="tasks-metric-icon-box" style={{ background: `${card.color}20`, color: card.color }}>
                      <CardIcon size={20} />
                    </div>
                    <div className="tasks-metric-content">
                      <div className="tasks-metric-value">
                        {card.value}
                      </div>
                      <div className="tasks-metric-label">{card.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Dashboard Widgets: 2-Column Grid */}
            <div className="tasks-dashboard-widgets">
              {/* Widget 1: Today's Tasks */}
              <div style={{ background: 'var(--bg-surface)', borderRadius: '14px', border: '1px solid var(--border-color)', padding: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Sun size={17} color="#f59e0b" />
                    <span>Today's Focus</span>
                  </h3>
                  <button onClick={() => navigate('/apps/tasks?view=today')} className="btn-secondary" style={{ padding: '3px 8px', fontSize: '11px' }}>
                    View All
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {dashboardData.widgets.todayTasks.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                      No tasks scheduled for today. Great job!
                    </div>
                  ) : (
                    dashboardData.widgets.todayTasks.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => setDetailDrawerTaskId(t.id)}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          background: 'var(--bg-secondary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{t.title}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{t.priority}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Widget 2: High Priority & Overdue */}
              <div style={{ background: 'var(--bg-surface)', borderRadius: '14px', border: '1px solid var(--border-color)', padding: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <AlertTriangle size={17} color="#ef4444" />
                    <span>Urgent & High Priority</span>
                  </h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {dashboardData.widgets.highPriorityTasks.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                      No critical tasks currently pending.
                    </div>
                  ) : (
                    dashboardData.widgets.highPriorityTasks.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => setDetailDrawerTaskId(t.id)}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          background: 'var(--bg-secondary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderLeft: '3px solid var(--danger)',
                        }}
                      >
                        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{t.title}</span>
                        <span style={{ fontSize: '11px', color: 'var(--danger)', fontWeight: 600 }}>{t.status}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Widget 3: Active Projects Progress */}
              <div style={{ background: 'var(--bg-surface)', borderRadius: '14px', border: '1px solid var(--border-color)', padding: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FolderKanban size={17} color="var(--accent-primary)" />
                    <span>Active Projects</span>
                  </h3>
                  <button onClick={() => navigate('/apps/tasks/projects')} className="btn-secondary" style={{ padding: '3px 8px', fontSize: '11px' }}>
                    All Projects
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {dashboardData.widgets.myProjects.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                      No active projects. Click + to create a project workspace.
                    </div>
                  ) : (
                    dashboardData.widgets.myProjects.map((p) => (
                      <div
                        key={p.id}
                        onClick={() => navigate(`/apps/tasks/projects/${p.id}`)}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          background: 'var(--bg-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{p.name}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--accent-primary)' }}>{p.progress}%</span>
                        </div>
                        <div style={{ height: '5px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${p.progress}%`, background: 'var(--accent-primary)' }} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Widget 4: Team Activity Feed */}
              <div style={{ background: 'var(--bg-surface)', borderRadius: '14px', border: '1px solid var(--border-color)', padding: '18px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 14px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Network size={17} color="#06b6d4" />
                  <span>Team Activity Stream</span>
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '240px', overflowY: 'auto' }}>
                  {dashboardData.widgets.teamActivity.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                      No recent team actions recorded.
                    </div>
                  ) : (
                    dashboardData.widgets.teamActivity.map((a) => (
                      <div key={a.id} style={{ padding: '8px 10px', borderRadius: '6px', background: 'var(--bg-secondary)', fontSize: '12px' }}>
                        <strong style={{ color: 'var(--accent-primary)' }}>{a.user_email || 'Teammate'}</strong>{' '}
                        <span style={{ color: 'var(--text-secondary)' }}>performed {a.action.toLowerCase().replace(/_/g, ' ')}</span>{' '}
                        {a.task_title && <strong style={{ color: 'var(--text-primary)' }}>"{a.task_title}"</strong>}
                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                          {new Date(a.created_at).toLocaleTimeString()}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ──────────────── CALENDAR VIEW ──────────────── */}
        {currentView === 'calendar' && (
          <TaskCalendarView
            tasks={tasks}
            onTaskClick={(t) => setDetailDrawerTaskId(t.id)}
          />
        )}

        {/* ──────────────── TIMELINE GANTT VIEW ──────────────── */}
        {currentView === 'timeline' && (
          <TaskTimelineView
            tasks={tasks}
            onTaskClick={(t) => setDetailDrawerTaskId(t.id)}
          />
        )}

        {/* ──────────────── TASK LIST / KANBAN VIEWS ──────────────── */}
        {currentView !== 'dashboard' && currentView !== 'calendar' && currentView !== 'timeline' && (
          <>
            {/* Desktop Filter Toolbar (> 640px) */}
            <div className="tasks-desktop-toolbar tasks-filter-toolbar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '220px' }}>
                <Search size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search tasks by title, description or tag..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    width: '100%',
                    fontSize: '13px',
                  }}
                />
              </div>

              <div className="tasks-filter-group">
                <select
                  className="filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="all">All Statuses</option>
                  <option value="BACKLOG">Backlog</option>
                  <option value="TODO">To Do</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="REVIEW">In Review</option>
                  <option value="BLOCKED">Blocked</option>
                  <option value="COMPLETED">Completed</option>
                </select>

                <select
                  className="filter-select"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  aria-label="Filter by priority"
                >
                  <option value="all">All Priorities</option>
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent ⚡</option>
                </select>

                <select
                  className="filter-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Filter by category"
                >
                  <option value="all">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>

                <button
                  onClick={loadData}
                  className="btn-secondary"
                  style={{ height: '38px', width: '38px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', flexShrink: 0 }}
                  title="Refresh tasks"
                >
                  <RefreshCw size={15} />
                </button>
              </div>
            </div>

            {/* Mobile Filter Bar (<= 640px) - Single Row Icon-Only */}
            <div className="tasks-mobile-filter-bar">
              {/* Search Toggle Icon */}
              <button
                onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
                className={`mobile-filter-icon-btn ${mobileSearchOpen || searchQuery ? 'active' : ''}`}
                title="Search tasks"
                aria-label="Toggle search input"
              >
                <Search size={16} />
              </button>

              {/* Status Filter Icon with native Select overlay */}
              <div
                className={`mobile-filter-select-wrapper ${statusFilter !== 'all' ? 'active' : ''}`}
                title={`Status: ${statusFilter}`}
              >
                <CircleDot size={16} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="all">All Statuses</option>
                  <option value="BACKLOG">Backlog</option>
                  <option value="TODO">To Do</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="REVIEW">In Review</option>
                  <option value="BLOCKED">Blocked</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>

              {/* Priority Filter Icon with native Select overlay */}
              <div
                className={`mobile-filter-select-wrapper ${priorityFilter !== 'all' ? 'active' : ''}`}
                title={`Priority: ${priorityFilter}`}
              >
                <Flag size={16} />
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  aria-label="Filter by priority"
                >
                  <option value="all">All Priorities</option>
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent ⚡</option>
                </select>
              </div>

              {/* Category Filter Icon with native Select overlay */}
              <div
                className={`mobile-filter-select-wrapper ${categoryFilter !== 'all' ? 'active' : ''}`}
                title="Filter by category"
              >
                <Tag size={16} />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Filter by category"
                >
                  <option value="all">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Reset Filters Icon Button (Shown when active) */}
              {(statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all' || searchQuery) && (
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setPriorityFilter('all');
                    setCategoryFilter('all');
                    setSearchQuery('');
                    setMobileSearchOpen(false);
                  }}
                  className="mobile-filter-icon-btn reset-btn"
                  title="Reset all filters"
                  aria-label="Reset filters"
                >
                  <X size={15} />
                </button>
              )}

              {/* Refresh Icon Button */}
              <button
                onClick={loadData}
                className="mobile-filter-icon-btn"
                title="Refresh tasks"
                aria-label="Refresh tasks"
              >
                <RefreshCw size={15} />
              </button>
            </div>

            {/* Expandable Mobile Search Bar */}
            {mobileSearchOpen && (
              <div className="tasks-mobile-search-expand">
                <Search size={15} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search tasks by title, tag..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="clear-search-btn"
                    aria-label="Clear search query"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}

            {/* List vs Board */}
            {viewMode === 'board' ? (
              <TaskKanbanBoard
                tasks={tasks}
                onTaskClick={(t) => setDetailDrawerTaskId(t.id)}
                onStatusChange={handleTaskStatusChange}
                onQuickAdd={(status) => {
                  setCreateModalStatus(status);
                  setCreateModalOpen(true);
                }}
              />
            ) : (
              <div className="tasks-table-container">
                <table className="tasks-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.02)', color: 'var(--text-secondary)', fontSize: '12px' }}>
                      <th style={{ padding: '12px 16px', width: '36px' }}></th>
                      <th style={{ padding: '12px 16px' }}>Task</th>
                      <th style={{ padding: '12px 16px' }}>Status</th>
                      <th style={{ padding: '12px 16px' }}>Priority</th>
                      <th style={{ padding: '12px 16px' }}>Subtasks</th>
                      <th style={{ padding: '12px 16px' }}>Due Date</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                          Loading tasks...
                        </td>
                      </tr>
                    ) : tasks.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                          {currentView === 'trash' ? 'Trash is empty.' : 'No tasks found matching your filters.'}
                        </td>
                      </tr>
                    ) : (
                      tasks.map((t) => (
                        <tr
                          key={t.id}
                          onClick={() => setDetailDrawerTaskId(t.id)}
                          style={{
                            borderBottom: '1px solid var(--border-color)',
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          {/* Favorite / Checkbox */}
                          <td style={{ padding: '12px 16px' }} onClick={(e) => handleToggleFavorite(e, t)}>
                            <Star size={16} fill={t.is_favorite ? '#eab308' : 'none'} color={t.is_favorite ? '#eab308' : 'var(--text-secondary)'} />
                          </td>

                          {/* Title & Project */}
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {t.title}
                            </div>
                            {t.project_name && (
                              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                📁 {t.project_name}
                              </div>
                            )}
                          </td>

                          {/* Status */}
                          <td style={{ padding: '12px 16px' }}>
                            <span
                              style={{
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: 600,
                                background: t.status === 'COMPLETED' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.15)',
                                color: t.status === 'COMPLETED' ? 'var(--success)' : 'var(--accent-primary)',
                              }}
                            >
                              {t.status}
                            </span>
                          </td>

                          {/* Priority */}
                          <td style={{ padding: '12px 16px' }}>
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: t.priority === 'URGENT' ? 'var(--danger)' : t.priority === 'HIGH' ? 'var(--warning)' : 'var(--text-secondary)',
                              }}
                            >
                              {t.priority}
                            </span>
                          </td>

                          {/* Subtasks Progress */}
                          <td style={{ padding: '12px 16px' }}>
                            {t.subtasks_total > 0 ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '120px' }}>
                                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{t.progress}%</span>
                                <div style={{ flex: 1, height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${t.progress}%`, background: 'var(--accent-primary)' }} />
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>—</span>
                            )}
                          </td>

                          {/* Due Date */}
                          <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {t.end_at ? new Date(t.end_at).toLocaleDateString() : '—'}
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '12px 16px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                            {currentView === 'trash' ? (
                              <button
                                onClick={(e) => handleRestoreTask(e, t.id)}
                                className="btn-secondary"
                                style={{ padding: '4px 8px', fontSize: '11px' }}
                              >
                                Restore
                              </button>
                            ) : (
                              <button
                                onClick={() => setShareModalTask(t)}
                                style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px' }}
                                title="Share Task"
                              >
                                <Share2 size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals & Slide-in Drawers */}
      <TaskCreateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => loadData()}
        projects={projects}
        categories={categories}
      />

      <TaskDetailDrawer
        taskId={detailDrawerTaskId}
        isOpen={Boolean(detailDrawerTaskId)}
        onClose={() => setDetailDrawerTaskId(null)}
        onTaskUpdated={() => loadData()}
        allProjectTasks={tasks}
      />

      {shareModalTask && (
        <TaskShareModal
          isOpen={Boolean(shareModalTask)}
          onClose={() => setShareModalTask(null)}
          taskId={shareModalTask.id}
          taskTitle={shareModalTask.title}
        />
      )}
    </MainLayout>
  );
}
